import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, LogOut, Lock, Loader2, FileText } from "lucide-react";
import { Logo } from "../components/Buttons";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) =>
    new Date(iso).toLocaleString("it-IT", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });

export default function AdminPage() {
    const [token, setToken] = useState(localStorage.getItem("admin_token") || "");
    const [password, setPassword] = useState("");
    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = async (t) => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/quote`, {
                headers: { Authorization: `Bearer ${t}` },
            });
            setQuotes(data.quotes);
        } catch (e) {
            toast.error("Sessione scaduta, effettua di nuovo il login.");
            localStorage.removeItem("admin_token");
            setToken("");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (token) load(token);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const login = async (e) => {
        e.preventDefault();
        try {
            const { data } = await axios.post(`${API}/admin/login`, { password });
            localStorage.setItem("admin_token", data.token);
            setToken(data.token);
            load(data.token);
        } catch (err) {
            toast.error(
                err.response?.status === 401
                    ? "Password errata."
                    : "Errore di connessione, riprova."
            );
        }
    };

    const logout = () => {
        localStorage.removeItem("admin_token");
        setToken("");
        setQuotes([]);
    };

    if (!token) {
        return (
            <div className="min-h-screen bg-bitume text-cloud flex items-center justify-center p-5">
                <form
                    onSubmit={login}
                    data-testid="admin-login-form"
                    className="w-full max-w-sm bg-panel border border-white/10 p-8 md:p-10 frame-clipped"
                >
                    <div className="flex justify-center mb-6">
                        <Logo />
                    </div>
                    <h1 className="font-display font-bold tracking-tight text-2xl text-center flex items-center justify-center gap-2">
                        <Lock className="w-5 h-5 text-ambra" />
                        Area riservata
                    </h1>
                    <p className="mt-2 text-sm text-ash text-center">
                        Inserisci la password per leggere i preventivi.
                    </p>
                    <input
                        type="password"
                        data-testid="admin-login-input"
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full mt-6 bg-bitume/60 border border-white/10 px-4 py-3.5 text-sm text-cloud placeholder:text-ash/50 outline-none focus:border-aqua transition-colors"
                        required
                    />
                    <button
                        type="submit"
                        data-testid="admin-login-button"
                        className="w-full mt-4 bg-ambra text-bitume font-semibold tracking-tight px-6 py-4 frame-clipped-sm transition-all duration-300 hover:brightness-110"
                    >
                        Entra
                    </button>
                    <a
                        href="/"
                        data-testid="admin-back-home"
                        className="block mt-6 text-center font-mono2 text-[10px] uppercase tracking-[0.2em] text-ash hover:text-cloud transition-colors"
                    >
                        ← Torna al sito
                    </a>
                </form>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-bitume text-cloud" data-testid="admin-dashboard">
            <header className="border-b border-white/5 bg-panel/60">
                <div className="max-w-5xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
                    <Logo />
                    <button
                        onClick={logout}
                        data-testid="admin-logout-button"
                        className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 font-mono2 text-[11px] uppercase tracking-[0.2em] text-ash hover:text-cloud hover:border-aqua transition-all frame-clipped-sm"
                    >
                        <LogOut className="w-4 h-4" />
                        Esci
                    </button>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-5 md:px-8 py-10">
                <div className="flex items-center justify-between mb-8">
                    <h1 className="font-display font-bold tracking-tight text-2xl flex items-center gap-3">
                        <FileText className="w-6 h-6 text-aqua" />
                        Richieste di preventivo
                    </h1>
                    <span
                        className="font-mono2 text-xs text-ash border border-white/10 px-3 py-1.5"
                        data-testid="admin-quote-count"
                    >
                        {quotes.length} {quotes.length === 1 ? "richiesta" : "richieste"}
                    </span>
                </div>

                {loading ? (
                    <div className="flex justify-center py-20">
                        <Loader2 className="w-8 h-8 animate-spin text-aqua" />
                    </div>
                ) : quotes.length === 0 ? (
                    <p className="text-ash text-sm py-20 text-center" data-testid="admin-empty-state">
                        Nessuna richiesta ancora. Le richieste dal form del sito compariranno qui.
                    </p>
                ) : (
                    <ul className="space-y-4" data-testid="admin-quotes-list">
                        {quotes.map((q) => (
                            <li
                                key={q.id}
                                data-testid="admin-quote-card"
                                className="bg-panel border border-white/10 p-6 md:p-7 frame-clipped-sm"
                            >
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div>
                                        <span className="font-mono2 text-[10px] uppercase tracking-[0.22em] text-aqua block mb-1.5">
                                            {q.servizio}
                                        </span>
                                        <h2 className="font-display font-semibold tracking-tight text-xl">
                                            {q.nome}
                                        </h2>
                                    </div>
                                    <span className="font-mono2 text-[10px] uppercase tracking-[0.18em] text-ash/70">
                                        {fmtDate(q.created_at)}
                                    </span>
                                </div>
                                <a
                                    href={`tel:${q.telefono}`}
                                    data-testid="admin-quote-phone"
                                    className="mt-4 inline-flex items-center gap-2 text-sm text-cloud hover:text-aqua transition-colors"
                                >
                                    <Phone className="w-4 h-4 text-ambra" />
                                    {q.telefono}
                                </a>
                                {q.messaggio && (
                                    <p className="mt-3 text-sm text-ash leading-relaxed border-l-2 border-white/10 pl-4">
                                        {q.messaggio}
                                    </p>
                                )}
                                <a
                                    href={`https://wa.me/${q.telefono.replace(/[^0-9]/g, "")}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="admin-quote-whatsapp"
                                    className="mt-4 inline-block font-mono2 text-[11px] uppercase tracking-[0.18em] text-wa hover:brightness-110 transition"
                                >
                                    Rispondi su WhatsApp →
                                </a>
                            </li>
                        ))}
                    </ul>
                )}
            </main>
        </div>
    );
}
